import { watchEffect } from "vue";
import { z } from "zod/v4";
import {
  useDumbStorage,
  type DumbStorageSerializer,
} from "../use-dumb-storage";

export interface OAuthFlowOptions {
  authUrl: string;
  onError: (error: string) => void;
  onCancel?: () => void;
}

import {
  ASYNC_FLOW_MESSAGE_EVENT_NAME,
  AsyncFlowMessageType,
} from "@f3/shared/async-flow-messages";
export { AsyncFlowMessageType };

const validateAuthenticationSuccessSchema = z.object({
  type: z.literal(AsyncFlowMessageType.ValidateAuthenticationSuccess),
  code: z.string(),
  state: z.string(),
  redirect_uri: z.string(),
});

const validateAuthenticationErrorSchema = z.object({
  type: z.literal(AsyncFlowMessageType.ValidateAuthenticationError),
  error: z.string(),
});

const closeWindowSchema = z.object({
  type: z.literal(AsyncFlowMessageType.CloseWindow),
});

const asyncFlowMessageSchema = z.object({
  type: z.literal(ASYNC_FLOW_MESSAGE_EVENT_NAME),
  data: z.discriminatedUnion("type", [
    validateAuthenticationSuccessSchema,
    validateAuthenticationErrorSchema,
    closeWindowSchema,
  ]),
});

type ValidateAuthenticationSuccess = z.infer<
  typeof validateAuthenticationSuccessSchema
>;

class ValidateAuthenticationSuccessSerializer implements DumbStorageSerializer<ValidateAuthenticationSuccess | null> {
  read(value: string | null) {
    if (!value) return null;

    try {
      const parsed = JSON.parse(value);
      const result = validateAuthenticationSuccessSchema.parse(parsed);

      return result;
    } catch {
      return null;
    }
  }

  write(value: ValidateAuthenticationSuccess | null) {
    return JSON.stringify(value);
  }
}

export function useAuthAsyncFlowManager(wind: Window) {
  const targetOrigin = new URLSearchParams(window.location.search).get(
    "origin",
  );

  const lastValidateAuthenticationMessage = useDumbStorage(
    "validate-authentication-message",
    null,
    {
      serializer: new ValidateAuthenticationSuccessSerializer(),
      storage: sessionStorage,
    },
  );

  function postMessage(message: z.infer<typeof asyncFlowMessageSchema>) {
    if (!targetOrigin) {
      console.error(
        "[AuthFlowManager] No target origin available, refusing to send",
      );
      return;
    }
    wind.postMessage(message, targetOrigin);
  }

  function sendCloseWindowMessage() {
    postMessage(
      asyncFlowMessageSchema.parse({
        type: ASYNC_FLOW_MESSAGE_EVENT_NAME,
        data: {
          type: AsyncFlowMessageType.CloseWindow,
        },
      }),
    );
  }

  function sendValidateAuthenticationMessage(
    response: z.infer<typeof validateAuthenticationSuccessSchema>,
    { saveOnSessionStorage = false }: { saveOnSessionStorage?: boolean } = {},
  ) {
    try {
      if (saveOnSessionStorage) {
        lastValidateAuthenticationMessage.value = response;
      }

      postMessage(
        asyncFlowMessageSchema.parse({
          type: ASYNC_FLOW_MESSAGE_EVENT_NAME,
          data: response,
        }),
      );
    } catch (error) {
      console.error(
        "[AuthFlowManager] Failed to send session response:",
        error,
      );
    }
  }

  function sendValidateAuthenticationError(
    response: z.infer<typeof validateAuthenticationErrorSchema>,
  ) {
    try {
      postMessage(
        asyncFlowMessageSchema.parse({
          type: ASYNC_FLOW_MESSAGE_EVENT_NAME,
          data: response,
        }),
      );
    } catch (error) {
      console.error(
        "[AuthFlowManager] Failed to send session response:",
        error,
      );
    }
  }

  function watchAsyncFlowMessage(
    listener: (
      response: z.infer<typeof asyncFlowMessageSchema>["data"],
    ) => void,
    { fetchLastMessage = false }: { fetchLastMessage?: boolean } = {},
  ) {
    watchEffect(() => {
      if (fetchLastMessage && lastValidateAuthenticationMessage.value) {
        listener(lastValidateAuthenticationMessage.value);
        lastValidateAuthenticationMessage.value = null;
      }

      function handleMessage(event: MessageEvent) {
        // This listener is used in same-origin popup flows only.
        // Cross-origin embed flows use use-embed-authentication-broker.ts instead.
        if (event.origin !== window.location.origin) return;
        if (event.data.type !== ASYNC_FLOW_MESSAGE_EVENT_NAME) return;

        const result = asyncFlowMessageSchema.safeParse(event.data);

        if (result.success) {
          listener(result.data.data);
        } else {
          console.error(
            `[AuthFlowManager] Received message with invalid data: ${result.error}`,
            event,
          );
        }
      }

      wind.addEventListener("message", handleMessage);

      return () => {
        wind.removeEventListener("message", handleMessage);
      };
    });
  }

  return {
    sendCloseWindowMessage,
    sendValidateAuthenticationMessage,
    sendValidateAuthenticationError,
    watchAsyncFlowMessage,
  };
}
